<template>
  <div class="gameView">
    <HeaderComponent v-if="showHeader" @open-shop="openShopModal" />
    <div class="mainContainer" :class="showHeader ? 'withHeader' : ''">
      <router-view></router-view>
    </div>
    <LateralButtons v-if="showHeader" />
    <PlayerStatusBar v-if="showHeader && isAuthenticated" />
    <FooterComponent />
    <ToastHost />
    <Loading v-if="isLoading" />
    <!-- Add ModalRankings component -->
    <ModalRankings />
    <ModalConfigs />
    <WeekCostsModal ref="weekCostsModal" />
    <ChangeLanguageModal ref="languageModal" />
    
    <!-- Monetization Components -->
    <PromotionSidebarButton v-if="showHeader" @open-shop="openShopModal" />
    <PromotionPopup v-if="showHeader && isAuthenticated" @open-shop="openShopModal" />
    <RevivalPopup v-if="showHeader && isAuthenticated" @open-premium-shop="openShopModal" />
    <BuyParalleliums :isOpen="showShopModal" @close="closeShopModal" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Loading from '@/shared/components/loading/LoadingPage.vue'
import HeaderComponent from '@/shared/header/HeaderComponent.vue'
import FooterComponent from '@/shared/footer/FooterComponent.vue'
import ToastHost from '@/shared/components/ToastHost.vue'
import LateralButtons from '@/shared/components/LateralButtons.vue'
import PlayerStatusBar from '@/components/PlayerStatusBar.vue'
import ModalRankings from '@/shared/components/ModalRankings.vue'
import ModalConfigs from '@/shared/components/ModalConfigs.vue'
import WeekCostsModal from '@/shared/components/weekCosts/WeekCostsModal.vue'
import ChangeLanguageModal from '@/shared/components/changeLanguageModal/ChangeLanguageModal.vue'
import PromotionSidebarButton from '@/shared/components/PromotionSidebarButton.vue'
import PromotionPopup from '@/shared/modals/PromotionPopup.vue'
import RevivalPopup from '@/shared/modals/RevivalPopup.vue'
import BuyParalleliums from '@/shared/modals/BuyParalleliums.vue'

export default {
  name: 'App',
  components: {
    Loading,
    HeaderComponent,
    FooterComponent,
    ToastHost,
    LateralButtons,
    PlayerStatusBar,
    ModalRankings,
    ModalConfigs,
    WeekCostsModal,
    ChangeLanguageModal,
    PromotionSidebarButton,
    PromotionPopup,
    RevivalPopup,
    BuyParalleliums
  },
  data() {
    return {
      isLoading: false,
      showShopModal: false
    }
  },
  computed: {
    ...mapState('auth', ['isAuthenticated']),
    ...mapState('player', ['loading as isLoading']),
    showHeader() {
      return this.$route.meta.requiresAuth && this.$route.name !== 'CreateNewCompany'
    }
  },
  watch: {
    // Watch for authentication changes to initialize monetization
    async isAuthenticated(newVal, oldVal) {
      // Only trigger when user becomes authenticated (not when app loads with existing auth)
      if (newVal && !oldVal) {
        console.log('User just logged in, initializing monetization...');
        
        try {
          // Load player data first
          if (!this.$store.state.player.playerData) {
            await this.$store.dispatch('player/loadPlayerData');
          }
          
          // Initialize monetization (load packages and promotions)
          await this.$store.dispatch('monetization/initializeMonetization');
          
          console.log('Monetization initialized after login');
        } catch (error) {
          console.error('Error initializing monetization after login:', error);
        }
      }
    }
  },
  async created() {
    this.isLoading = true;
    try {
      await this.tryAutoLogin();
      
      // Check if language preference exists in localStorage
      if (!localStorage.getItem('language')) {
        this.$nextTick(() => {
          // Show language selection modal if no preference is saved
          this.$refs.languageModal.showModal();
        });
      }
      
      // If authenticated (auto-login successful), initialize monetization
      if (this.$store.state.auth.isAuthenticated) {
        // Load player data first
        if (!this.$store.state.player.playerData) {
          await this.$store.dispatch('player/loadPlayerData');
        }
        
        // Initialize monetization (load packages and promotions)
        await this.$store.dispatch('monetization/initializeMonetization');
        
        // Process weekly financials if player is authenticated and player data exists
        if (this.$store.state.player.playerData) {
          const financialResult = await this.$store.dispatch('player/processWeeklyFinancials');
          
          // Show the week costs modal with financial data if we have results
          if (financialResult && financialResult.success) {
            this.$nextTick(() => {
              this.$refs.weekCostsModal.showModal(financialResult);
            });
          }
        }
      }
    } catch (error) {
      console.error('Auto login error:', error);
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    ...mapActions('auth', ['tryAutoLogin']),
    
    openShopModal() {
      this.showShopModal = true;
    },
    
    closeShopModal() {
      this.showShopModal = false;
    }
  }
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.gameView{
  position: relative;
  .mainContainer{
    min-height: calc(100vh - 100px);
    &.withHeader{
      padding: 80px 0 40px;
    }
  }
}

@media (max-width: 768px) {
  .mainContainer{
    &.withHeader{
      padding: 110px 0 18px !important;
    }
  }
}

body {
  font-family: 'Cinzel Decorative', serif, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
}

#app {
  min-height: 100vh;
}

/* ==================================
   CYBERPUNK SCROLLBAR - GLOBAL THEME
   ================================== */
   
/* Webkit browsers (Chrome, Safari, Edge) */
::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

::-webkit-scrollbar-track {
  background: linear-gradient(180deg, #0d0d0d 0%, #1a1a1a 100%);
  border-radius: 6px;
  border: 1px solid rgba(255, 70, 70, 0.2);
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.8);
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #ff4646 0%, #ff1a1a 50%, #cc0000 100%);
  border-radius: 6px;
  border: 1px solid rgba(255, 70, 70, 0.3);
  box-shadow: 
    0 0 10px rgba(255, 70, 70, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 -1px 0 rgba(0, 0, 0, 0.2);
  
  /* Cyberpunk glow effect */
  position: relative;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #ff6666 0%, #ff4646 50%, #ff0000 100%);
  box-shadow: 
    0 0 15px rgba(255, 70, 70, 0.8),
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    inset 0 -1px 0 rgba(0, 0, 0, 0.3);
  transform: scale(1.05);
  transition: all 0.2s ease;
}

::-webkit-scrollbar-thumb:active {
  background: linear-gradient(180deg, #ffd700 0%, #ffcc00 50%, #ff9900 100%);
  box-shadow: 
    0 0 20px rgba(255, 215, 0, 0.9),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

::-webkit-scrollbar-corner {
  background: #0d0d0d;
  border: 1px solid rgba(255, 70, 70, 0.2);
}

/* Thin scrollbar variant for specific components */
.scrollbar-thin::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #3498db 0%, #2980b9 100%);
  border-radius: 4px;
  box-shadow: 0 0 8px rgba(52, 152, 219, 0.4);
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #5dade2 0%, #3498db 100%);
  box-shadow: 0 0 12px rgba(52, 152, 219, 0.6);
}

/* Firefox scrollbar */
* {
  scrollbar-width: thin;
  scrollbar-color: #ff4646 #1a1a1a;
}

/* Custom scrollbar for Firefox with more detailed styling */
@supports (scrollbar-color: red blue) {
  * {
    scrollbar-color: #ff4646 rgba(26, 26, 26, 0.9);
    scrollbar-width: auto;
  }
}

/* Mobile touch scrolling enhancement */
* {
  -webkit-overflow-scrolling: touch;
}

/* Smooth scrolling behavior */
html {
  scroll-behavior: smooth;
}

/* Mobile responsive scrollbars */
@media (max-width: 768px) {
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  .scrollbar-thin::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
}

@media (max-width: 480px) {
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  .scrollbar-thin::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }
  
  ::-webkit-scrollbar-thumb {
    border-radius: 3px;
  }
}

/* Special scrollbars for specific areas */

/* Modal scrollbars */
.modal-container::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #3498db 0%, #2980b9 100%);
  box-shadow: 0 0 10px rgba(52, 152, 219, 0.5);
}

.modal-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #5dade2 0%, #3498db 100%);
  box-shadow: 0 0 15px rgba(52, 152, 219, 0.7);
}

/* Chat/Message area scrollbar */
.chat-container::-webkit-scrollbar,
.message-container::-webkit-scrollbar {
  width: 8px;
}

.chat-container::-webkit-scrollbar-thumb,
.message-container::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(255, 215, 0, 0.8) 0%, rgba(255, 165, 0, 0.8) 100%);
  border-radius: 4px;
  box-shadow: 0 0 6px rgba(255, 215, 0, 0.4);
}

.chat-container::-webkit-scrollbar-thumb:hover,
.message-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #ffd700 0%, #ffa500 100%);
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
}

/* Scrollbar animations and transitions */
::-webkit-scrollbar-thumb {
  transition: all 0.3s ease;
}

/* Dark theme scrollbar for specific components */
.dark-scroll::-webkit-scrollbar-track {
  background: #0a0a0a;
  border: 1px solid rgba(255, 70, 70, 0.1);
}

.dark-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(255, 70, 70, 0.8) 0%, rgba(204, 0, 0, 0.8) 100%);
}

/* Premium/VIP scrollbar variant */
.premium-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, 
    #ffd700 0%, 
    #ffcc00 25%, 
    #ff9900 50%,
    #ff4646 75%,
    #cc0000 100%);
  box-shadow: 
    0 0 15px rgba(255, 215, 0, 0.6),
    0 0 30px rgba(255, 70, 70, 0.4);
}

.premium-scroll::-webkit-scrollbar-thumb:hover {
  transform: scale(1.1);
  box-shadow: 
    0 0 20px rgba(255, 215, 0, 0.8),
    0 0 40px rgba(255, 70, 70, 0.6);
}
</style>
